import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/api";
import SummaryCards from "@/components/SummaryCards";
import HoldingsTable from "@/components/HoldingsTable";
import AddHoldingModal from "@/components/AddHoldingModal";
import ImportCsvModal from "@/components/ImportCsvModal";
import ProjectedIncomeChart from "@/components/ProjectedIncomeChart";
import DividendCalendar from "@/components/DividendCalendar";
import Recommendations from "@/components/Recommendations";
import AnalystSignals from "@/components/AnalystSignals";
import RebalanceSuggestions from "@/components/RebalanceSuggestions";
import Toast from "@/components/Toast";
import AiChat from "@/components/AiChat";
import BriefingButton from "@/components/BriefingButton";
import { Plus, LogOut, RefreshCcw, Upload } from "lucide-react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [projected, setProjected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [modalPrefill, setModalPrefill] = useState({ ticker: "", cost: "" });
  const [toast, setToast] = useState({ open: false, message: "", onUndo: null });

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, c, p] = await Promise.all([
        api.get("/portfolio/summary"),
        api.get("/portfolio/calendar"),
        api.get("/portfolio/projected-income"),
      ]);
      setSummary(s.data);
      setCalendar(c.data);
      setProjected(p.data);
    } catch (e) {
      console.error("Failed to load dashboard", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleDelete = async (id, ticker) => {
    // Capture the holding data so we can undo if user changes their mind
    const removed = summary?.holdings?.find((h) => h.id === id);
    // Optimistic UI: remove from state instantly so it feels snappy
    setSummary((prev) =>
      prev ? { ...prev, holdings: prev.holdings.filter((h) => h.id !== id), holding_count: prev.holding_count - 1 } : prev
    );
    try {
      await api.delete(`/holdings/${id}`);
      // Show toast with undo option
      setToast({
        open: true,
        message: `Removed ${ticker} from your portfolio`,
        onUndo: async () => {
          if (!removed) return;
          try {
            await api.post("/holdings", {
              ticker: removed.ticker,
              shares: removed.shares,
              cost_basis: removed.cost_basis,
            });
            loadAll();
          } catch (e) {
            console.error("Undo failed", e);
          }
        },
      });
      loadAll();
    } catch (e) {
      console.error("Delete failed", e);
      loadAll(); // rollback via refresh if it failed
    }
  };

  const openAddModal = (prefill = { ticker: "", cost: "" }) => {
    setModalPrefill(prefill);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen grain pb-16">
      {/* Header */}
      <header className="border-b sticky top-0 z-30" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
        <div className="max-w-[1500px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-7 h-7 border" style={{ borderColor: "var(--gain)" }}>
              <div className="w-full h-full" style={{ background: "var(--gain)", opacity: 0.7 }} />
            </div>
            <div>
              <p className="text-xs tracking-[0.3em] uppercase">DIVIDEND TRACKER</p>
              <p className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                {user?.name} · {user?.email}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <BriefingButton />
            <button
              data-testid="refresh-button"
              className="btn-secondary inline-flex items-center gap-2"
              onClick={loadAll}
              disabled={refreshing}
            >
              <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Syncing" : "Refresh"}
            </button>
            <button
              data-testid="open-import-csv-button"
              className="btn-secondary inline-flex items-center gap-2"
              onClick={() => setImportOpen(true)}
              title="Import from broker CSV"
            >
              <Upload size={12} /> Import
            </button>
            <button
              data-testid="open-add-holding-button"
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => openAddModal()}
            >
              <Plus size={14} /> Add holding
            </button>
            <button data-testid="logout-button" className="btn-ghost p-2" onClick={logout} title="Logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 py-8 space-y-8">
        {/* Hero */}
        <section className="fade-up">
          <p className="label-xs mb-3">{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
          <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight">
            Your money is <span style={{ color: "var(--gain)" }}>working</span> while you sleep.
          </h1>
          <p className="text-xs mt-3 max-w-xl" style={{ color: "var(--text-secondary)" }}>
            Live snapshot of projected dividend income from your portfolio. Quotes & dividend fundamentals from Alpha Vantage (cached 5min / 24hr).
          </p>
        </section>

        {/* Summary */}
        {loading ? (
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Loading portfolio<span className="blink">█</span></div>
        ) : (
          <SummaryCards summary={summary} />
        )}

        {/* Chart + Calendar */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <ProjectedIncomeChart data={projected} />
            </div>
            <DividendCalendar events={calendar?.events} />
          </div>
        )}

        {/* Rebalance Suggestions */}
        {!loading && summary?.holdings?.length > 0 && (
          <RebalanceSuggestions
            suggestions={summary?.suggestions}
            onQuickAdd={openAddModal}
            onScrollToTicker={(t) => {
              const row = document.querySelector(`[data-testid="holding-row-${t}"]`);
              if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
          />
        )}

        {/* Holdings table */}
        {!loading && (
          <section>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-serif text-2xl">Holdings</h2>
              <p className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                {summary?.holding_count || 0} positions
              </p>
            </div>
            <HoldingsTable holdings={summary?.holdings || []} onDelete={handleDelete} />
          </section>
        )}

        {/* Analyst Signals — buy/sell recommendations for your holdings */}
        {!loading && summary?.holdings?.length > 0 && (
          <AnalystSignals holdings={summary.holdings} />
        )}

        {/* Recommendations */}
        {!loading && (
          <Recommendations
            onAdd={({ ticker, price }) => openAddModal({ ticker, cost: price })}
          />
        )}
      </main>

      <AddHoldingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdded={loadAll}
        initialTicker={modalPrefill.ticker}
        initialCost={modalPrefill.cost}
      />
      <ImportCsvModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={loadAll}
      />
      <Toast
        open={toast.open}
        message={toast.message}
        onUndo={toast.onUndo}
        onDismiss={() => setToast((t) => ({ ...t, open: false }))}
      />
      <AiChat />
    </div>
  );
}
